// Fixed top nav for marketing pages. Wordmark left, auth CTAs right, mobile
// Sheet wraps the CTAs on small screens. Transparent at top of page, gains a
// backdrop blur + border once scrolled past the hero. Text-only wordmark for
// now — drop an SVG into public/ and swap the span for an <img> when one
// exists. Nav links (Data sources / Reports / More) removed per issue 863.

import { Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { buttonVariants } from '#app/components/ui/button.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { cn } from '#app/lib/utils.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { NavUser } from './nav-user.tsx'
import { SiteContainer } from './site-container.tsx'
import { GlobalSearchPalette } from './global-search-palette.tsx'

// Marketing nav links (Data sources / Reports / More) removed per ledgerai
// issue 863 — content those linked to is archived at `archive/ambitious-homepage`
// and the homepage/more routes are blanked pending the wallet-input rewrite.

export function SiteTopNav() {
	const [scrolled, setScrolled] = useState(false)
	const [mobileOpen, setMobileOpen] = useState(false)
	// Auth-aware CTA: signed-in visitors see "Dashboard →" instead of
	// "Log in". The shell around the CTA doesn't change — same top bar,
	// same nav links, same wordmark. This matches Cloudflare's marketing
	// behaviour (cloudflare.com right-CTA is "Dashboard" for authed).
	// Only auth-aware change in SiteTopNav per 848 step 6.
	const user = useOptionalUser()
	// Hide "See examples →" when already on a public-book surface —
	// otherwise the CTA is self-referential. The public surface is
	// entirely under `/gallery/*` (index picker + cookie-scoped
	// blotter). Nothing under `/books/*` — that's authed-only chrome.
	const location = useLocation()
	// /gallery routes are retired (1032); no public book-browse surface
	// exists today. If a public-example surface returns, add it here.
	const onPublicSurface = false

	useEffect(() => {
		function onScroll() {
			setScrolled(window.scrollY > 24)
		}
		onScroll()
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [])

	return (
		<header
			className={cn(
				'fixed inset-x-0 top-0 z-50 transition-colors duration-200',
				scrolled
					? 'bg-background/80 border-border/60 border-b backdrop-blur-md'
					: 'bg-transparent',
			)}
		>
			<SiteContainer className="flex h-14 items-center justify-between gap-4 md:grid md:grid-cols-[auto_1fr_auto]">
				{/* Left: wordmark. Nav links removed per issue 863. */}
				<Link to="/" className="flex shrink-0 items-center gap-2">
					<span className="text-base font-semibold tracking-tight">
						LedgerAI
					</span>
				</Link>

				{/* Center: global wallet search — wallet-first IA (protocol-
				    agnostic identity, constant across future observer
				    additions). Position drill happens inside the blotter
				    after wallet selection. Hidden on mobile; the mobile
				    Sheet doesn't carry it yet. */}
				<div className="hidden md:flex md:justify-center">
					<GlobalSearchPalette />
				</div>

				{/* Right column: CTAs. Signed-in visitors see "Your books →"
				    (linking to /books). Anon visitors see "Log in" +
				    "Request invitation →" (→ X DM). Self-serve retired per
				    1032; invites arranged over X. Matches the hero copy. */}
				<div className="hidden items-center gap-2 md:flex">
					{user ? (
						<>
							<Link
								to="/books"
								className={cn(buttonVariants({ size: 'sm' }))}
							>
								Your books →
							</Link>
							{/* Same account dropdown as the app header — one component,
							    one settings tree, one place to change auth-nav shape. */}
							<NavUser />
						</>
					) : (
						<>
							<Link
								to="/login"
								className={cn(
									buttonVariants({ size: 'sm', variant: 'ghost' }),
								)}
							>
								Log in
							</Link>
							{onPublicSurface ? null : (
								<a
									href="https://x.com/underemployed"
									target="_blank"
									rel="noopener noreferrer"
									className={cn(buttonVariants({ size: 'sm' }))}
								>
									Request invitation →
								</a>
							)}
						</>
					)}
				</div>

				<Popover open={mobileOpen} onOpenChange={setMobileOpen}>
					<PopoverTrigger
						className="border-border bg-background hover:bg-secondary/60 flex size-10 items-center justify-center rounded-xl border shadow-sm transition-colors md:hidden"
						aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
					>
						{mobileOpen ? (
							<X className="size-4" />
						) : (
							<Menu className="size-4" />
						)}
					</PopoverTrigger>
					<PopoverContent align="end" className="w-56 p-2">
						<div className="flex flex-col gap-2">
							{user ? (
								<div className="flex items-center gap-2">
									<Link
										to="/books"
										onClick={() => setMobileOpen(false)}
										className={cn(buttonVariants({ size: 'sm' }), 'flex-1')}
									>
										Your books →
									</Link>
									<NavUser />
								</div>
							) : (
								<>
									<Link
										to="/login"
										onClick={() => setMobileOpen(false)}
										className={cn(
											buttonVariants({ size: 'sm', variant: 'ghost' }),
											'w-full',
										)}
									>
										Log in
									</Link>
									{onPublicSurface ? null : (
										<a
											href="https://x.com/underemployed"
											target="_blank"
											rel="noopener noreferrer"
											onClick={() => setMobileOpen(false)}
											className={cn(buttonVariants({ size: 'sm' }), 'w-full')}
										>
											Request invitation →
										</a>
									)}
								</>
							)}
						</div>
					</PopoverContent>
				</Popover>
			</SiteContainer>
		</header>
	)
}
